import config from "../../config";
import { AnnotationToolAccess } from "../../shared/models/User";
import PhysicianTask from "../models/annotations/PhysicianTask";
import Dataset, { DatasetData, MediaStats } from "../models/annotations/Dataset";
import AnnotationTool, { AnnotationToolData } from "../models/annotations/AnnotationTool";
import AnnotationType from "../models/annotations/AnnotationType";
import Task from "../models/annotations/Task";
import AbstractCommunicationController, { ApiError } from "../../shared/api/AbstractCommunicationController";
import TaskStatus, {
    AssignmentType,
    PhysicianStatus,
    TaskConflictsMatrix,
    TaskData
} from "../models/annotations/TaskStatus";
import Conflict, { ConflictDetails } from "../models/annotations/Conflict";

class AnnotationCommunicationController extends AbstractCommunicationController {
    private endpoints = {
        GET_TASKS: "/task",
        GET_PHYSICIAN_TASKS: "/task/physician",
        GET_TASK_STATUS: "/task/:id",
        GET_ANNOTATIONS: "/task/:id/annotation",
        ANNOTATION_TOOL_ACCESS: "/annotation-tool/access",
        GRANT_TOOL_ACCESS: "/annotation-tool/:id/access",
        REVOKE_TOOL_ACCESS: "/annotation-tool/:id/access",
        GET_DATASET: "/dataset",
        GET_DATASET_DETAILS: "/dataset/:id",
        GET_ANNOTATION_TOOLS: "/annotation-tool",
        GET_ANNOTATION_INTERFACES: "/annotation-tool/:id/interface",
        UPDATE_ANNOTATION_TOOL: "/annotation-tool/:id",
        GET_PRINT_FUNCTIONS: "/annotation-function/print",
        GET_CONFLICT_FUNCTIONS: "/annotation-function/conflict",
        NEW_ANNOTATION_INTERFACE: "/annotation-tool/:id/interface",
        UPDATE_ANNOTATION_INTERFACE: "/annotation-tool/:tool_id/interface/:id",
        DELETE_DATASET: "/dataset/:id",
        DELETE_ANNOTATION_TOOL: "/annotation-tool/:id",
        DELETE_ANNOTATION_INTERFACE: "/annotation-tool/:tool_id/interface/:id",
        NEW_DATASET: "/dataset",
        NEW_ANNOTATION_TOOL: "/annotation-tool",
        NEW_TASK: "/task",
        DELETE_TASK: "/task/:id",
        ASSIGN_TASK: "/task/:id/assignment",
        REMOVE_ASSIGNMENT: "/task/:id/assignment/:user_id",
        GET_CONFLICT_MATRIX: "/task/:id/conflicts/compare",
        GET_MEDIA_STATS: "/media/stats",
        GET_MEDIA_TO_SEGMENT: "/media/segmentation",
        GET_MEDIA_DETAILS: "/media/:id",
        GET_VISIT_DETAILS: "/visit/:id",
        GET_PATIENT_MEDIA: "/media/patient",
        GET_TASK_CONFLICTS: "/task/:id/conflicts",
        GET_TASK_CONFLICT_DETAILS: "/task/:id/conflict",
        ACCEPT_CONFLICT: "/task/conflict/accept",
        POST_ANNOTATION: "/task/annotation"
    };

    getPhysicianTasks = async (id: number, cnt?: number, offset?: number): Promise<PhysicianTask[]> => {
        const tasks: PhysicianTask[] = await this.get(this.endpoints.GET_PHYSICIAN_TASKS, {
            physician: id,
            cnt,
            offset
        });

        return tasks.map((task: any) => new PhysicianTask(task));
    };

    getTasks = async (filter: string = "", cnt: number = 10, offset: number = 0): Promise<Task[]> => {
        const tasks: Task[] = await this.get(this.endpoints.GET_TASKS, {
            filter,
            cnt,
            offset
        });

        return tasks.map((task: Task) => new Task(task));
    };

    getTaskStatus = async (id: number): Promise<TaskStatus> => {
        const task: TaskStatus = await this.get(this.formatEndpoint(this.endpoints.GET_TASK_STATUS, { id }));
        return new TaskStatus(task);
    };

    getMediaToSegment = async (
        physician: number,
        task: number,
        cnt: number = 20
    ): Promise<any[]> => {
        const media: any[] = await this.get(this.endpoints.GET_MEDIA_TO_SEGMENT, {
            physician,
            task,
            cnt
        });

        // The deprecated endpoint still returns `media` and `fullpath`, while
        // the current Swagger contract calls them `id` and omits `fullpath`.
        // Normalize both response versions for the segmentation UI.
        return media.map(item => ({
            ...item,
            id: item.id ?? item.media
        }));
    };

    getMediaDetails = async (id: number): Promise<any> => {
        return this.get(this.formatEndpoint(this.endpoints.GET_MEDIA_DETAILS, { id }), {
            video_format: "mp4"
        });
    };

    getVisitDetails = async (visitId: number): Promise<any> => {
        return this.get(this.formatEndpoint(this.endpoints.GET_VISIT_DETAILS, { id: visitId }));
    };

    getPatientMedia = async (patient: string | number, visit: number): Promise<any[]> => {
        return this.get(this.endpoints.GET_PATIENT_MEDIA, {
            patient: String(patient),
            visit,
            type: "image"
        });
    };

    getUserAnnotationTool = async (physician: number): Promise<AnnotationToolAccess[]> => {
        const res: AnnotationToolAccess[] = await this.get(this.endpoints.ANNOTATION_TOOL_ACCESS, {
            physician
        });

        return res.map((access: any) => {
            return {
                id: access.annotation_tool,
                name: access.annotation_tool_name,
                endpoint: access.endpoint ?? "",
                access: access.access
            };
        });
    };

    grantUserAnnotationToolAccess = async (
        physician: number,
        annotationTool: number,
        endpoint: string
    ): Promise<string> => {
        const res: { instructions?: string } = await this.post(
            this.formatEndpoint(this.endpoints.GRANT_TOOL_ACCESS, { id: annotationTool }),
            {
                physician: String(physician),
                endpoint
            }
        );
        return res.instructions ?? "";
    };

    revokeUserAnnotationToolAccess = async (physician: number, annotationTool: number): Promise<void> => {
        await this.delete(this.formatEndpoint(this.endpoints.REVOKE_TOOL_ACCESS, { id: annotationTool }), {
            physician: String(physician)
        });
    };

    getDatasets = async (): Promise<any[]> => {
        const datasets: Dataset[] = await this.get(this.endpoints.GET_DATASET);

        return datasets.map((dataset: Dataset) => new Dataset(dataset));
    };

    getDataset = (id: number): Promise<any> => {
        return this.get(this.formatEndpoint(this.endpoints.GET_DATASET_DETAILS, { id }));
    };

    getAnnotationTools = async (): Promise<AnnotationTool[]> => {
        const annotationTools: AnnotationTool[] = await this.get(this.endpoints.GET_ANNOTATION_TOOLS);

        return annotationTools.map((annotationTool: AnnotationTool) => {
            return new AnnotationTool(annotationTool);
        });
    };

    getAnnotationTypes = async (annotationTool: number): Promise<AnnotationType[]> => {
        const annotationTypes: AnnotationType[] = await this.get(
            this.formatEndpoint(this.endpoints.GET_ANNOTATION_INTERFACES, { id: annotationTool })
        );

        return annotationTypes.map((annotationType: AnnotationType) => {
            return new AnnotationType(annotationType);
        });
    };

    updateAnnotationTool = async (annotationTool: number, data: AnnotationToolData) => {
        await this.patch(this.formatEndpoint(this.endpoints.UPDATE_ANNOTATION_TOOL, { id: annotationTool }), data);
    };

    getPrintFunctions = async (): Promise<string[]> => {
        return this.get(this.endpoints.GET_PRINT_FUNCTIONS);
    };

    getConflictFunctions = async (): Promise<string[]> => {
        return this.get(this.endpoints.GET_CONFLICT_FUNCTIONS);
    };

    newAnnotationInterface = async (
        annotation_tool: number,
        data: {
            name: string;
            description: string;
            instructions: string;
            xml: string;
            print_function: string;
            conflict_functions: string[];
        }
    ): Promise<AnnotationType> => {
        const res: AnnotationType = await this.post(
            this.formatEndpoint(this.endpoints.NEW_ANNOTATION_INTERFACE, { id: annotation_tool }),
            {
                name: data.name,
                description: data.description,
                instructions: data.instructions,
                xml: data.xml,
                print_function: data.print_function,
                conflict_functions: data.conflict_functions
            }
        );

        return new AnnotationType(res);
    };

    updateAnnotationInterface = async (
        annotationTool: number,
        annotationType: number,
        data: {
            name: string;
            instructions: string;
            xml: string;
            print_function: string;
            conflict_functions: string[];
        }
    ) => {
        await this.patch(
            this.formatEndpoint(this.endpoints.UPDATE_ANNOTATION_INTERFACE, {
                tool_id: annotationTool,
                id: annotationType
            }),
            data
        );
    };

    deleteDataset = async (dataset: number) => {
        await this.delete(this.formatEndpoint(this.endpoints.DELETE_DATASET, { id: dataset }));
    };

    deleteAnnotationTool = async (annotationTool: number) => {
        await this.delete(this.formatEndpoint(this.endpoints.DELETE_ANNOTATION_TOOL, { id: annotationTool }));
    };

    deleteAnnotationType = async (annotationTool: number, annotationType: number) => {
        await this.delete(
            this.formatEndpoint(this.endpoints.DELETE_ANNOTATION_INTERFACE, {
                tool_id: annotationTool,
                id: annotationType
            })
        );
    };

    newDataset = async (data: DatasetData): Promise<Dataset> => {
        // POST /dataset è create(@Body() data: DatasetCreationData): con @Body() l'intero
        // body della richiesta È il DatasetCreationData, campi allo stesso livello.
        const res: Dataset = await this.post(this.endpoints.NEW_DATASET, data);
        return new Dataset(res);
    };

    newAnnotationTool = async (data: AnnotationToolData): Promise<AnnotationTool> => {
        const res: AnnotationTool = await this.post(this.endpoints.NEW_ANNOTATION_TOOL, data);
        return new AnnotationTool(res);
    };

    newAnnotationTask = async (data: TaskData): Promise<Task> => {
        const res: Task = await this.post(this.endpoints.NEW_TASK, data, undefined, 40000);
        return new Task(res);
    };

    deleteAnnotationTask = async (task: number) => {
        await this.delete(this.formatEndpoint(this.endpoints.DELETE_TASK, { id: task }));
    };

    // POST /task/:task_id/assignment accetta un solo {user, deadline} alla volta
    // (TaskAssignmentCreationParams) - va richiamato una volta per medico, sia per
    // assegnare un task a un nuovo medico sia per aggiornarne solo la deadline (il
    // server distingue da solo i due casi in base a se il medico è già assegnato).
    // user è tipizzato string lato server (v. TaskAssignmentCreationParams), da qui
    // il cast esplicito: il modello lato client lo tratta come number ovunque.
    updateTaskAssignment = async (taskId: number, assignment: AssignmentType): Promise<PhysicianStatus[]> => {
        const result: any = await this.post(
            this.formatEndpoint(this.endpoints.ASSIGN_TASK, { id: taskId }),
            {
                user: String(assignment.user),
                deadline: assignment.deadline
            },
            undefined,
            40000
        );
        return result.physicians;
    };

    removeTaskAssignment = async (taskId: number, userId: number): Promise<void> => {
        await this.delete(
            this.formatEndpoint(this.endpoints.REMOVE_ASSIGNMENT, { id: taskId, user_id: String(userId) })
        );
    };

    getConflictMatrix = async (task: number, fn: string): Promise<TaskConflictsMatrix> => {
        const matrix: TaskConflictsMatrix = await this.get(
            this.formatEndpoint(this.endpoints.GET_CONFLICT_MATRIX, { id: task }),
            {
                fn
            }
        );
        matrix.fn = fn;
        return matrix;
    };

    getMediaStats = (): Promise<MediaStats> => {
        return this.get(this.endpoints.GET_MEDIA_STATS);
    };

    getTaskConflicts = async (
        task: number,
        annotator: number,
        conflict_annotator: number,
        conflict_function: string,
        cnt: number = 20,
        offset: number = 0
    ): Promise<Conflict[]> => {
        const res: Conflict[] = await this.get(this.formatEndpoint(this.endpoints.GET_TASK_CONFLICTS, { id: task }), {
            annotator,
            conflict_annotator,
            fn: conflict_function,
            cnt,
            offset
        });

        return res.map((conf: any) => new Conflict(annotator, conflict_annotator, conflict_function, task, conf));
    };

    getConflictDetails(
        task: number,
        annotator: number,
        conflict_annotator: number,
        media: number,
        conflict_function: string
    ): Promise<ConflictDetails> {
        return this.get(this.formatEndpoint(this.endpoints.GET_TASK_CONFLICT_DETAILS, { id: task }), {
            annotator,
            conflict_annotator,
            media,
            fn: conflict_function
        });
    }

    acceptConflict = (annotation: number, accepted_annotation: number): Promise<void> => {
        return this.patch(this.endpoints.ACCEPT_CONFLICT, {
            annotation,
            accepted_annotation
        });
    };

    getAnnotations = (task: number): Promise<any> => {
        return this.get(this.formatEndpoint(this.endpoints.GET_ANNOTATIONS, { id: task }));
    };

    isCancelled = (err: ApiError) => {
        return err.isCancelled ?? false;
    };

    saveAnnotation = async (
        physician: number,
        task: number,
        payload: any
    ): Promise<any> => {
        return this.post(
            this.endpoints.POST_ANNOTATION,
            payload,
            {
                physician: String(physician),
                task: String(task)
            }
        );
    };

}

const instance = new AnnotationCommunicationController(config.MAIN_API_URL);

export default instance;
